// @flow
import type { DeviceMode, DeviceModel, DeviceType, Region } from '~/core/types/devices';
import generalSvgs from '../icons/general-svg';

export const GROUP = 'group';

export const deviceTypes = {
    ACTIVE_PLUG: 'activeplug',
    BOILER_MODULE: 'boilermodule',
    BULB: 'bulb',
    COLOR_TUNEABLE_LIGHT: 'colourtuneablelight',
    COLOR_LIGHT: 'colourlight',
    CONTACT_SENSOR: 'contactsensor',
    HEATING: 'heating',
    HIVE_BUTTON: 'hivebutton',
    HIVE_CAMERA: 'hivecamera',
    HOT_WATER: 'hotwater',
    HUB: 'hub',
    HUE_BRIDGE: 'philipshuebridge',
    LEAK_SENSOR: 'leakdetector',
    MOTION_SENSOR: 'motionsensor',
    NA_THERMOSTAT: 'nathermostat',
    PRODUCT_GROUP: 'productgroup',
    SENSE: 'sense',
    SIGNAL_BOOSTER: 'signalbooster',
    SMART_PLUG: 'smartplug',
    THERMOSTAT_UI: 'thermostatui',
    TUNEABLE_LIGHT: 'tuneablelight',
    WARM_WHITE_LIGHT: 'warmwhitelight',
    THERMOSTAT: 'thermostat'
};

export const deviceImages: Map<string, string> = new Map([
    [deviceTypes.ACTIVE_PLUG, 'default'],
    [deviceTypes.BOILER_MODULE, 'receiver'],
    [deviceTypes.BULB, 'white-light'],
    [deviceTypes.COLOR_TUNEABLE_LIGHT, 'colour-light'],
    [deviceTypes.COLOR_LIGHT, 'colour-light'],
    [deviceTypes.CONTACT_SENSOR, 'contact-sensor'],
    [deviceTypes.HEATING, 'default'],
    [deviceTypes.HOT_WATER, 'default'],
    [deviceTypes.HUB, 'hub'],
    [deviceTypes.LEAK_SENSOR, 'leak'],
    [deviceTypes.MOTION_SENSOR, 'motion-sensor'],
    [deviceTypes.NA_THERMOSTAT, 'default'],
    [deviceTypes.SENSE, 'sense'],
    [deviceTypes.SIGNAL_BOOSTER, 'signal-booster'],
    [deviceTypes.SMART_PLUG, 'default'],
    [deviceTypes.THERMOSTAT_UI, 'default'],
    [deviceTypes.TUNEABLE_LIGHT, 'tuneable-light'],
    [deviceTypes.WARM_WHITE_LIGHT, 'white-light'],
    [deviceTypes.THERMOSTAT, 'default'],
    [deviceTypes.HUE_BRIDGE, 'hue-bridge'],
    [deviceTypes.HIVE_BUTTON, 'button'],
    [deviceTypes.HIVE_CAMERA, 'camera'],
    [GROUP, 'group']
]);

export const batteryMap: Map<number, string> = new Map([
    [0, generalSvgs.BATTERY_EMPTY],
    [1, generalSvgs.BATTERY_LOW],
    [2, generalSvgs.BATTERY_LOW],
    [3, generalSvgs.BATTERY_NORMAL],
    [4, generalSvgs.BATTERY_NORMAL],
    [5, generalSvgs.BATTERY_FULL]
]);

export const wifiMap: Map<number, string> = new Map([
    [0, generalSvgs.WIFI_0_BARS],
    [1, generalSvgs.WIFI_1_BARS],
    [2, generalSvgs.WIFI_2_BARS],
    [3, generalSvgs.WIFI_3_BARS]
]);

export const settingTypes = {
    DELETE: 'DELETE',
    DELETE_HUE: 'DELETE_HUE',
    HOLIDAY_MODE: 'HOLIDAY_MODE',
    INFORMATION: 'INFORMATION',
    NOTIFICATIONS: 'NOTIFICATIONS',
    PARTNER_INFO: 'PARTNER_INFO',
    PRIVACY_SETTINGS: 'PRIVACY_SETTINGS',
    REBOOT_HUB: 'REBOOT_HUB',
    RENAME: 'RENAME',
    EDIT_GROUP: 'EDIT_GROUP',
    DELETE_GROUP: 'DELETE_GROUP',
    OPTIMUM_START: 'OPTIMUM_START', // Annoyingly have to name this to Optimum Start becuase of platform, but this should be referred to as Ready By in all future code
    // Camera specific settings
    CAMERA_VIDEO: 'CAMERA_VIDEO',
    CAMERA_DETECTION: 'CAMERA_DETECTION',
    CAMERA_DEVICE: 'CAMERA_DEVICE',
    CHANGE_WIFI: 'CHANGE_WIFI'
};

export const signalMap: Map<number, string> = new Map([
    [0, generalSvgs.LINK_BROKEN],
    [1, generalSvgs.SIGNAL_1_BARS],
    [2, generalSvgs.SIGNAL_2_BARS],
    [3, generalSvgs.SIGNAL_3_BARS],
    [4, generalSvgs.SIGNAL_4_BARS],
    [5, generalSvgs.SIGNAL_4_BARS]
]);

export const categories = {
    HUE: 'HUE',
    ZONE: 'ZONE',
    DEVICE: 'DEVICE'
};

// $FlowFixMe
export const mapCategoryToRemoveFunction = (category, deviceHandler, zoneHandler, hueHandler) => {
    const functionMap = new Map([
        [category === categories.ZONE, zoneHandler],
        [category === categories.DEVICE, deviceHandler],
        [category === categories.HUE, hueHandler]
    ]);
    return functionMap.get(true);
};

export const connectionTypes = {
    WIFI: 'wifi',
    WIRED: 'wired'
};

export const heatingTypes = [
    deviceTypes.THERMOSTAT_UI,
    deviceTypes.HEATING,
    deviceTypes.HOT_WATER,
    deviceTypes.NA_THERMOSTAT
];

export const thermostatDeviceTypes = [deviceTypes.THERMOSTAT_UI, deviceTypes.NA_THERMOSTAT, deviceTypes.THERMOSTAT];

export const powerSourceTypes = {
    MAINS: 'mains',
    BATTERY: 'battery'
};

export const devicesWithModelVariations: Array<DeviceType> = [
    deviceTypes.BOILER_MODULE,
    deviceTypes.HUB,
    deviceTypes.THERMOSTAT,
    deviceTypes.THERMOSTAT_UI,
    deviceTypes.ACTIVE_PLUG
];

export const regions: { [Region]: Region } = {
    EU: 'EU',
    IN: 'IN',
    NA: 'NA',
    UK: 'UK'
};

export const hublessDeviceTypes = [deviceTypes.LEAK_SENSOR, deviceTypes.HIVE_CAMERA];

export const fanModes = {
    AUTO: 'AUTO',
    CIRCULATE: 'CIRCULATE',
    ALWAYS: 'MANUAL'
};

export const circulateDurations = {
    FIFTEEN: '15',
    THIRTY: '30',
    FORTYFIVE: '45'
};

export const gu10Models = {
    FWBULB01UK: 'FWBulb01UK',
    FWGU10BULBUK: 'FWGU10BulbUK',
    FWGU10BULB01UK: 'FWGU10Bulb01UK',
    TWGU10BULB01UK: 'TWGU10Bulb01UK'
};

export const candlelightModels = {
    FWCLBulb01UK: 'FWCLBulb01UK',
    TWCLBulb01UK: 'TWCLBulb01UK'
};

export const bulbTypes = [
    deviceTypes.COLOR_LIGHT,
    deviceTypes.COLOR_TUNEABLE_LIGHT,
    deviceTypes.TUNEABLE_LIGHT,
    deviceTypes.WARM_WHITE_LIGHT
];

export const bulbModes = {
    colour: 'COLOUR',
    white: 'WHITE'
};

export const plugTypes = [deviceTypes.ACTIVE_PLUG, deviceTypes.SMART_PLUG];

export const deviceCaps = {
    HIVE2_UPGRADE: 'HIVE2_UPGRADE',
    SLT3B_REPLACEMENT: 'SLT3B_REPLACEMENT'
};

export const deviceModes: { [string]: DeviceMode } = {
    BOOST: 'BOOST',
    MANUAL: 'MANUAL',
    OFF: 'OFF',
    ON: 'ON',
    SCHEDULE: 'SCHEDULE',
    HOLD: 'HOLD',
    EMERGENCY_HEAT: 'EMERGENCY_HEAT',
    MIMIC: 'MIMIC'
};

export const deviceModels: { [DeviceModel]: string } = {
    ALB1: 'ALB1',
    CS1: 'CS1',
    MS1: 'MS1',
    NANO1: 'NANO1',
    NANO2: 'NANO2',
    SENSE: 'SENSE',
    SLB1: 'SLB1',
    SLB2: 'SLB2',
    SLB3: 'SLB3',
    SLP2: 'SLP2',
    SLP2b: 'SLP2b',
    SLP2c: 'SLP2c',
    SLP2d: 'SLP2d',
    SLR1: 'SLR1',
    SLR2: 'SLR2',
    SLT1: 'SLT1',
    SLT2: 'SLT2',
    SLT3: 'SLT3',
    SLT3b: 'SLT3b', // this device is a replacement for broken SLT3s
    SLT4: 'SLT4',
    SPG1: 'SPG1'
};

export const deviceErrors = {
    FIRMWARE_UPDATE_REQUIRED: 'FIRMWARE_UPDATE_REQUIRED',
    WRONG_PRODUCT_FOUND: 'WRONG_PRODUCT_FOUND',
    MALFORMED_NODE_DESCRIPTOR: 'MALFORMED_NODE_DESCRIPTOR'
};

export const deviceCapabilities = {
    FAN: 'fan',
    DUAL: 'dual',
    HUMIDITY: 'humidity',
    HUMIDITY_READ: 'humidity-read',
    HEAT: 'heat',
    COOL: 'cool',
    EMERGENCY_HEAT: 'emergency-heat'
};

export const deviceControlModes = {
    HEAT: 'HEAT',
    COOL: 'COOL',
    AUTO: 'AUTO',
    OFF: 'OFF'
};

export const deviceHumidityModes = {
    UNKNOWN: 'UNKNOWN',
    HUMIDIFIER: 'HUMIDIFIER',
    DEHUMIDIFIER: 'DEHUMIDIFIER'
};

export const pmzStatusTypes = {
    OK: 'OK',
    PMZ_WRONG_PRODUCT: 'PMZ_WRONG_PRODUCT',
    PMZ_FIRMWARE: 'PMZ_FIRMWARE'
};

export const manufacturers = {
    PHILIPS: 'Philips'
};

export const capabilities = {
    ACTIONS_2: 'ACTIONS_2',
    BOOST: 'BOOST',
    EMERGENCY_HEAT: 'EMERGENCY_HEAT',
    HOLIDAY_MODE: 'HOLIDAY_MODE',
    INSTALL_PHILIPS_HUE: 'INSTALL_PHILIPS_HUE',
    LIGHT_COLOUR: 'LIGHT_COLOUR',
    LIGHT_TUNEABLE: 'LIGHT_TUNEABLE',
    LIGHT_WHITE: 'LIGHT_WHITE',
    MIGRATE_NANO1: 'MIGRATE_NANO1',
    MIMIC_MODE: 'MIMIC_MODE',
    SCHEDULE: 'SCHEDULE',
    PRODUCT_GROUP: 'PRODUCT_GROUP',
    IDENTIFY_DEVICE: 'IDENTIFY_DEVICE'
};

export const hubOperationModes = {
    FAILURE: 'FAILURE',
    MIGRATING: 'MIGRATING'
};

export const removableDevicesByManufacturers = [manufacturers.PHILIPS];

export const hubModelTypes = [deviceModels.NANO1, deviceModels.NANO2, deviceModels.SENSE];

export const receiverModelTypes = [deviceModels.SLR1, deviceModels.SLR2];

export const thermostatModelTypes = [
    deviceModels.SLT1,
    deviceModels.SLT2,
    deviceModels.SLT3,
    deviceModels.SLT3b,
    deviceModels.SLT4
];

export const thermostatsWithReceivers = [deviceModels.SLT2, deviceModels.SLT3, deviceModels.SLT3b];

export const motionSensorPrefixModelTypes = ['PIR', 'MOT'];

export const contactSensorPrefixModelTypes = ['WDS', 'DWS'];

export const plugModelTypes = [deviceModels.SLP2b, deviceModels.SLP2c, deviceModels.SLP2d, deviceModels.SLP2];

export const signalBoosterModelTypes = [deviceModels.SLB1, deviceModels.SLB2, deviceModels.SLB3];

export const gu10DimmableModelTypes = [gu10Models.FWBULB01UK, gu10Models.FWGU10BULBUK, gu10Models.FWGU10BULB01UK];
export const gu10TuneableModelTypes = [gu10Models.TWGU10BULB01UK];
export const candlelightDimmableModelTypes = [candlelightModels.FWCLBulb01UK];
export const candlelightTuneableModelTypes = [candlelightModels.TWCLBulb01UK];



// WEBPACK FOOTER //
// ./src/core/constants/devices/index.js